import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { STORE_OPTION_VALUES } from "../constants/store-options";

interface StoreOption {
  value: string;
  label: string;
}

interface StoreComboboxProps {
  options: StoreOption[];
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

export function StoreCombobox({
  options,
  value,
  onSelect,
  placeholder = "Selecione uma loja...",
  searchPlaceholder = "Buscar loja...",
  emptyMessage = "Nenhuma loja encontrada.",
  className,
  disabled = false,
  loading = false,
}: StoreComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find(option => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between min-w-[200px]",
            !selectedOption && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          {selectedOption ? (
            <span className="flex items-center gap-1 min-w-0">
              {selectedOption.value === STORE_OPTION_VALUES.ALL_STORES ? (
                selectedOption.label
              ) : (
                <>
                  <span className="flex-shrink-0 font-medium">
                    {selectedOption.value}
                  </span>
                  <span className="flex-shrink-0">-</span>
                  <span className="truncate">
                    {selectedOption.label}
                  </span>
                </>
              )}
            </span>
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] max-w-[400px] p-0" 
        side="bottom"
        align="start"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={8}
      >
        <Command className="max-h-[300px]">
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            {loading ? (
              <div className="py-2 px-4 text-sm text-muted-foreground">
                Carregando lojas...
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={`${option.value} ${option.label}`}
                      onSelect={() => {
                        onSelect(option.value);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="flex items-center gap-1 min-w-0">
                        {option.value === STORE_OPTION_VALUES.ALL_STORES ? (
                          option.label
                        ) : (
                          <>
                            <span className="flex-shrink-0 font-medium">
                              {option.value}
                            </span>
                            <span className="flex-shrink-0">-</span>
                            <span className="truncate">
                              {option.label}
                            </span>
                          </>
                        )}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
